import React from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';

// Data for Canadian work experience points
const canadianExperiencePoints = [
  { years: 'None or less than a year', withSpouse: 0, withoutSpouse: 0 },
  { years: '1 year', withSpouse: 35, withoutSpouse: 40 },
  { years: '2 years', withSpouse: 46, withoutSpouse: 53 },
  { years: '3 years', withSpouse: 56, withoutSpouse: 64 },
  { years: '4 years', withSpouse: 63, withoutSpouse: 72 },
  { years: '5 years or more', withSpouse: 70, withoutSpouse: 80 },
];

// Data for foreign work experience combined with language proficiency
const foreignExperienceLanguagePoints = [
  { years: 'None', clb7_8: 0, clb9: 0 },
  { years: '1 or 2 years', clb7_8: 13, clb9: 25 },
  { years: '3 years or more', clb7_8: 25, clb9: 50 },
];

// Data for foreign work experience combined with Canadian work experience
const foreignExperienceCanadianExperiencePoints = [
  { years: 'None', oneYearCanadian: 0, twoOrMoreYearsCanadian: 0 },
  { years: '1 or 2 years', oneYearCanadian: 13, twoOrMoreYearsCanadian: 25 },
  { years: '3 years or more', oneYearCanadian: 25, twoOrMoreYearsCanadian: 50 },
];

// Data for spouse's Canadian work experience points
const spouseCanadianExperiencePoints = [
  { years: 'None or less than a year', points: 0 },
  { years: '1 year', points: 5 },
  { years: '2 years', points: 7 },
  { years: '3 years', points: 8 },
  { years: '4 years', points: 9 },
  { years: '5 years or more', points: 10 },
];

const WorkExperienceComponent = ({
  formData,
  calculateCanadianExperiencePoints,
  calculateForeignExperiencePoints,
  calculateSpouseExperiencePoints,
}) => (
  <Box>
    <Typography variant="h6">Work Experience</Typography>
    <Typography>
      Work experience, both within Canada and abroad, significantly influences your CRS score. Combining foreign work experience with Canadian
      experience and strong language proficiency can maximize your points.
    </Typography>
    {formData?.canadianExperience && (
      <Typography>
        Points for Canadian work experience: {calculateCanadianExperiencePoints(formData.canadianExperience)}
      </Typography>
    )}
    {formData?.foreignExperience && (
      <Typography>
        Points for foreign work experience: {calculateForeignExperiencePoints(formData.foreignExperience)}
      </Typography>
    )}
    {formData?.spouseExperience && (
      <Typography>
        Points for spouse's Canadian work experience: {calculateSpouseExperiencePoints(formData.spouseExperience)}
      </Typography>
    )}

    {/* Canadian Work Experience Breakdown */}
    <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
      Canadian Work Experience Breakdown
    </Typography>
    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Years of Canadian Work Experience</TableCell>
            <TableCell align="right">Points (With Spouse)</TableCell>
            <TableCell align="right">Points (Without Spouse)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {canadianExperiencePoints.map((row) => (
            <TableRow key={row.years}>
              <TableCell component="th" scope="row">
                {row.years}
              </TableCell>
              <TableCell align="right">{row.withSpouse}</TableCell>
              <TableCell align="right">{row.withoutSpouse}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Foreign Work Experience Combined with Language Proficiency */}
    <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
      Foreign Work Experience Combined with Language Proficiency
    </Typography>
    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Years of Foreign Work Experience</TableCell>
            <TableCell align="right">Points (CLB 7 or 8)</TableCell>
            <TableCell align="right">Points (CLB 9 or more)</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {foreignExperienceLanguagePoints.map((row) => (
            <TableRow key={row.years}>
              <TableCell component="th" scope="row">
                {row.years}
              </TableCell>
              <TableCell align="right">{row.clb7_8}</TableCell>
              <TableCell align="right">{row.clb9}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Foreign Work Experience Combined with Canadian Work Experience */}
    <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
      Foreign Work Experience Combined with Canadian Work Experience
    </Typography>
    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Years of Foreign Work Experience</TableCell>
            <TableCell align="right">Points with 1 Year Canadian Experience</TableCell>
            <TableCell align="right">Points with 2 or More Years Canadian Experience</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {foreignExperienceCanadianExperiencePoints.map((row) => (
            <TableRow key={row.years}>
              <TableCell component="th" scope="row">
                {row.years}
              </TableCell>
              <TableCell align="right">{row.oneYearCanadian}</TableCell>
              <TableCell align="right">{row.twoOrMoreYearsCanadian}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>

    {/* Spouse's Canadian Work Experience Breakdown */}
    <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
      Spouse's Canadian Work Experience Breakdown
    </Typography>
    <TableContainer component={Paper} sx={{ marginTop: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Years of Canadian Work Experience</TableCell>
            <TableCell align="right">Points</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {spouseCanadianExperiencePoints.map((row) => (
            <TableRow key={row.years}>
              <TableCell component="th" scope="row">
                {row.years}
              </TableCell>
              <TableCell align="right">{row.points}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
);

export default WorkExperienceComponent;
